import { permissionsArray } from './appRouteMappings';
import { SALESFORCE_CONTACT_EMAIL_KEY } from './constants';
import { Permissions } from './types';
import { getOrgIdAndOrgCode } from 'src/services/api';
import { createSalesforceContact } from 'src/services/apiV1';
import store from 'src/store/store';
import { stripBase, withBase } from 'src/utils/basePath';
import { getUserRole, Permission, Routes, UserRoles } from 'src/utils/enums';

interface LocalStorageOrgData {
  org_id: number;
  org_name: string;
}

/*
    callback function to refresh all tabs whenever a super admin
    has selected a new org
 */
export const goToHomePage = async (e: StorageEvent) => {
  const rawLocalStorageOrgData = window.localStorage.getItem('org');
  const userState = store.getState().user;
  const userRole = userState.role;
  const isSuperAdmin = userRole === UserRoles.ROLE_SUPER_ADMIN;
  const isOrgStorageKey = e.key === 'org';
  const shouldReturnEarly =
    !rawLocalStorageOrgData || !isSuperAdmin || !isOrgStorageKey;

  if (shouldReturnEarly) {
    return;
  }

  try {
    const data = await getOrgIdAndOrgCode();
    const orgName = data.organization_id;

    const localStorageOrgData: LocalStorageOrgData = JSON.parse(
      rawLocalStorageOrgData,
    );

    if (orgName && localStorageOrgData.org_name !== orgName) {
      document.location.href = withBase('/');
    }
  } catch {
    return;
  }
};

export const getTabsFromPermissions = (permissionKeys: string[]): string[] => {
  return permissionKeys.map((permKey: string) => {
    return permissionsArray[permKey].tab;
  });
};

export const getPermissionKeys = (permissions: Permissions): string[] => {
  return Object.keys(permissions)
    .filter(permKey => permissions[permKey]) // this removes keys without permissions
    .filter(permKey => permissionsArray[permKey]) // removes invalid permissions
    .sort();
};

// Mirrors of FLAMETHROWER: role-based users already get these tabs from it, so only grants add them.
const FLAMETHROWER_MIRROR_KEYS: ReadonlySet<string> = new Set([
  Permission.ACTION_BUILDER,
  Permission.SOLVE_INSIGHTS_VIEW,
]);

/** Keys that contribute navigation tabs; routes still use every key from `getPermissionKeys`. */
export const getTabPermissionKeys = (permissions: Permissions): string[] =>
  getPermissionKeys(permissions).filter(
    permKey =>
      !(
        FLAMETHROWER_MIRROR_KEYS.has(permKey) &&
        permissions[Permission.FLAMETHROWER]
      ),
  );

export const getIsValidRoute = () => {
  const userLocation = stripBase(location.pathname) as Routes;

  const routes = [
    Routes.SOLVE,
    Routes.ACTION_BUILDER,
    Routes.ACTION_BUILDER_EDIT,
    Routes.WORKFLOW_BUILDER,
    Routes.WORKFLOW_BUILDER_EDIT,
    Routes.SOLVE_LITE,
    Routes.SOLVE_CONFIGURATION,
    Routes.SOLVE_PREVIEW,
    Routes.LOOKER_ANALYTICS,
    Routes.PREDICT_INTENTS,
    Routes.SOLVE_INSIGHTS_MAIN,
    Routes.LOG,
    Routes.SOLVE_API_CONFIGURATION,
    Routes.AGENT_QA,
  ];

  return userLocation.length > 0 && routes.includes(userLocation);
};

export const safeConvertToNumber = (str: string | null) => {
  if (str === null) {
    return null;
  }

  const parsedNumber = Number(str);

  if (isNaN(parsedNumber)) {
    return null;
  }

  return parsedNumber;
};

export const getStoredSalesforceContactEmail = (): string | null => {
  return localStorage.getItem(SALESFORCE_CONTACT_EMAIL_KEY);
};

export const setStoredSalesforceContactEmail = (email: string): void => {
  localStorage.setItem(SALESFORCE_CONTACT_EMAIL_KEY, email);
};

// The server can only create Salesforce contacts from staging and production.
const ENVIRONMENTS_WITHOUT_SALESFORCE = ['local', 'development'];

export const processAuthenticatedUser = async (userEmail: string) => {
  if (
    ENVIRONMENTS_WITHOUT_SALESFORCE.includes(ENVIRONMENT) ||
    getUserRole() == UserRoles.ROLE_SUPER_ADMIN ||
    getStoredSalesforceContactEmail() === userEmail
  ) {
    return;
  }

  try {
    const response = await createSalesforceContact();
    if (response?.success) {
      setStoredSalesforceContactEmail(userEmail);
    }
  } catch {
    // Contact sync must not interrupt sign-in; request failures are already reported.
    return;
  }
};
